/**
 * Appels JSON vers le serveur.
 *
 * Toute panne est RENDUE, jamais avalée : l'appelant reçoit un message lisible et
 * c'est l'écran qui décide quoi en faire. Un `catch` vide ici ferait afficher
 * « tout va bien » alors que rien ne répond.
 */
export class ErreurApi extends Error {
  constructor(
    message: string,
    readonly statut?: number,
  ) {
    super(message);
    this.name = 'ErreurApi';
  }
}

export interface Resultat<T> {
  ok: boolean;
  donnees?: T;
  message?: string;
}

export async function lireJson<T>(
  chemin: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<T>> {
  const { fetchImpl = fetch, signal } = options;

  try {
    const reponse = await fetchImpl(chemin, {
      headers: { Accept: 'application/json' },
      // La session vit dans un cookie `HttpOnly` : sans cette option, un appel
      // vers un autre port (3100 → 3101 en développement) partirait anonyme.
      credentials: 'same-origin',
      ...(signal ? { signal } : {}),
    });

    if (!reponse.ok) {
      // Le serveur explique ses refus : on préfère SON message au nôtre.
      let message = `Le serveur répond ${reponse.status}.`;
      try {
        const corps = (await reponse.json()) as { error?: unknown };
        if (typeof corps.error === 'string' && corps.error.trim() !== '') {
          message = corps.error;
        }
      } catch {
        /* corps non JSON : le statut suffit */
      }
      return { ok: false, message };
    }

    return { ok: true, donnees: (await reponse.json()) as T };
  } catch (erreur) {
    if (erreur instanceof DOMException && erreur.name === 'AbortError') {
      throw erreur;
    }
    return {
      ok: false,
      message: 'Serveur injoignable — vérifiez qu’il tourne sur le port 3101.',
    };
  }
}

/**
 * En-tête exigé par le serveur sur toute écriture : une page tierce ne peut pas
 * le poser (il faudrait un pré-vol CORS autorisé). Il est donc envoyé ici, une
 * fois, pour tous les appels qui modifient quelque chose.
 */
export const ENTETE_ECRITURE = 'x-presta-csrf';

/** Écrit (POST/PATCH) et rend le corps JSON, ou le message de refus du serveur. */
export async function ecrireJson<T>(
  chemin: string,
  methode: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  corps: unknown,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<T>> {
  const { fetchImpl = fetch, signal } = options;

  try {
    const reponse = await fetchImpl(chemin, {
      method: methode,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        [ENTETE_ECRITURE]: '1',
      },
      credentials: 'same-origin',
      body: methode === 'DELETE' ? undefined : JSON.stringify(corps),
      ...(signal ? { signal } : {}),
    });

    if (!reponse.ok) {
      let message = `Le serveur répond ${reponse.status}.`;
      try {
        const refus = (await reponse.json()) as { error?: unknown };
        if (typeof refus.error === 'string' && refus.error.trim() !== '') {
          message = refus.error;
        }
      } catch {
        /* corps non JSON : le statut suffit */
      }
      return { ok: false, message };
    }

    // 204 (et 205) n'ont pas de corps : lire du JSON ferait échouer un succès.
    if (reponse.status === 204 || reponse.status === 205) {
      return { ok: true, donnees: undefined as T };
    }

    return { ok: true, donnees: (await reponse.json()) as T };
  } catch (erreur) {
    if (erreur instanceof DOMException && erreur.name === 'AbortError') {
      throw erreur;
    }
    return { ok: false, message: 'Serveur injoignable — vérifiez qu’il tourne sur le port 3101.' };
  }
}
